import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface ToastMessage {
  id: string;
  type: ToastType;
  message: string;
  onUndo?: () => void | Promise<void>;
  duration?: number; // Duration in milliseconds
}

interface ToastContextProps {
  showToast: (message: string, type?: ToastType, onUndo?: () => void | Promise<void>, duration?: number) => void;
  showUndoToast: (message: string, onUndo: () => void | Promise<void>, duration?: number) => void;
  showSuccess: (message: string, duration?: number) => void;
  showError: (message: string, duration?: number) => void;
  showWarning: (message: string, duration?: number) => void;
  showInfo: (message: string, duration?: number) => void;
  removeToast: (id: string) => void;
  toasts: ToastMessage[];
}

const ToastContext = createContext<ToastContextProps | undefined>(undefined);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastMessage[]>([]);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const showToast = useCallback((
    message: string, 
    type: ToastType = 'info', 
    onUndo?: () => void | Promise<void>,
    duration?: number
  ) => {
    const id = Date.now().toString() + Math.random().toString(36).substr(2, 9);
    
    // Default durations
    let defaultDuration = 4000;
    if (type === 'error') defaultDuration = 8000;
    if (onUndo) defaultDuration = 6000; // longer for undo

    const finalDuration = duration || defaultDuration;

    setToasts((prev) => [...prev, { id, type, message, onUndo, duration: finalDuration }]);

    if (finalDuration > 0) {
      setTimeout(() => {
        removeToast(id);
      }, finalDuration);
    }
  }, [removeToast]);

  const showUndoToast = useCallback((message: string, onUndo: () => void | Promise<void>, duration?: number) => {
    showToast(message, 'success', onUndo, duration);
  }, [showToast]);

  const showSuccess = useCallback((message: string, duration?: number) => {
    showToast(message, 'success', undefined, duration);
  }, [showToast]);

  const showError = useCallback((message: string, duration?: number) => {
    showToast(message, 'error', undefined, duration);
  }, [showToast]);

  const showWarning = useCallback((message: string, duration?: number) => {
    showToast(message, 'warning', undefined, duration);
  }, [showToast]);

  const showInfo = useCallback((message: string, duration?: number) => {
    showToast(message, 'info', undefined, duration);
  }, [showToast]);

  return (
    <ToastContext.Provider value={{
      showToast,
      showUndoToast,
      showSuccess,
      showError,
      showWarning,
      showInfo,
      removeToast,
      toasts
    }}>
      {children}
    </ToastContext.Provider>
  );
}

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}
